/**
 * Connector API Client — calls the RetailsyncGo backend for actual data import.
 *
 * Endpoints:
 *   POST /api/v1/connector/import  — import parsed + mapped data into the database
 *   GET  /api/v1/connector/health  — health check
 */

import { API_ORIGIN, ApiError, getAuthHeaders, readApiResponse, describeLicenseError } from '../config/api'

interface ImportRequest {
  tenantId: string
  branchId?: string
  entity: string
  records: Record<string, any>[]
  conflictStrategy: 'external_wins' | 'internal_wins' | 'merge' | 'manual_review'
  dryRun?: boolean
}

interface ImportResponse {
  success: boolean
  imported: number
  failed: number
  skipped: number
  errors: Array<{ row: number; field?: string; message: string }>
  jobId?: string
}

export async function importData(req: ImportRequest): Promise<ImportResponse> {
  const url = `${API_ORIGIN}/api/v1/connector/import`
  const resp = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(req),
  })

  // 207 = partial success; a 402 (unlicensed tenant) must read as such rather
  // than as "Import failed (402)".
  if (!resp.ok && resp.status !== 207) {
    const payload = await resp.json().catch(() => ({}))
    const apiError = new ApiError(payload.error || payload.message || `Import failed (${resp.status})`, resp.status, payload)
    throw new Error(apiError.licenseRequired ? describeLicenseError(apiError).message : apiError.message)
  }

  return readApiResponse<ImportResponse>(resp)
}

export async function connectorHealth(): Promise<{ status: string; version: string }> {
  const url = `${API_ORIGIN}/api/v1/connector/health`
  const resp = await fetch(url, { headers: getAuthHeaders() })
  return readApiResponse<{ status: string; version: string }>(resp)
}
